// SPDX-License-Identifier: MIT
/**
 * Vector paths: parse SVG path data into polylines, measure them, draw them on progressively,
 * resample, morph and scatter points along them. This is how a brand's real logo vectors drive
 * draw-on, morphs and particle assemblies (instead of a generic logo animation).
 */
import { lerp } from '../core/math.ts';
export type Pt = [
    number,
    number
];
export type Subpath = {
    points: Pt[];
    closed: boolean;
};
export type Box = {
    x: number;
    y: number;
    w: number;
    h: number;
};
const NUM = /[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/y;
function tokenize(d: string): Array<string | number> {
    const out: Array<string | number> = [];
    let i = 0;
    while (i < d.length) {
        const c = d[i]!;
        if (/[MmLlHhVvCcSsQqTtAaZz]/.test(c)) {
            out.push(c);
            i++;
        }
        else if (/[\s,]/.test(c)) {
            i++;
        }
        else {
            NUM.lastIndex = i;
            const m = NUM.exec(d);
            if (!m)
                throw new Error(`invalid path data near "${d.slice(i, i + 10)}"`);
            out.push(Number(m[0]));
            i = NUM.lastIndex;
        }
    }
    return out;
}
/**
 * Flattening density: one segment per 6 units of length, and at least one per 5° of turn. Logos are
 * drawn hundreds of px tall from small viewBoxes, so length alone leaves visible facets on tight curves.
 */
const MAX_TURN = Math.PI / 36;
const segmentsFor = (len: number, turn: number) => Math.min(64, Math.max(4, Math.ceil(len / 6), Math.ceil(turn / MAX_TURN)));
/** Total turning of a control polygon (radians); zero-length legs are skipped. */
function turnOf(pts: Pt[]): number {
    let turn = 0;
    let prev: Pt | undefined;
    for (let i = 1; i < pts.length; i++) {
        const d: Pt = [pts[i]![0] - pts[i - 1]![0], pts[i]![1] - pts[i - 1]![1]];
        if (d[0] === 0 && d[1] === 0)
            continue;
        if (prev)
            turn += Math.abs(Math.atan2(prev[0] * d[1] - prev[1] * d[0], prev[0] * d[0] + prev[1] * d[1]));
        prev = d;
    }
    return turn;
}
function cubic(p0: Pt, p1: Pt, p2: Pt, p3: Pt, out: Pt[]) {
    const n = segmentsFor(dist(p0, p1) + dist(p1, p2) + dist(p2, p3), turnOf([p0, p1, p2, p3]));
    for (let i = 1; i <= n; i++) {
        const t = i / n;
        const u = 1 - t;
        out.push([
            u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0],
            u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1],
        ]);
    }
}
function quad(p0: Pt, p1: Pt, p2: Pt, out: Pt[]) {
    cubic(p0, [p0[0] + (2 / 3) * (p1[0] - p0[0]), p0[1] + (2 / 3) * (p1[1] - p0[1])], [p2[0] + (2 / 3) * (p1[0] - p2[0]), p2[1] + (2 / 3) * (p1[1] - p2[1])], p2, out);
}
/** SVG elliptical arc (endpoint parameterisation, SVG 1.1 §F.6.5). */
function arc(p0: Pt, rx: number, ry: number, phiDeg: number, large: boolean, sweep: boolean, p1: Pt, out: Pt[]) {
    if (rx === 0 || ry === 0) {
        out.push(p1);
        return;
    }
    const phi = (phiDeg * Math.PI) / 180;
    const cos = Math.cos(phi);
    const sin = Math.sin(phi);
    const dx = (p0[0] - p1[0]) / 2;
    const dy = (p0[1] - p1[1]) / 2;
    const x1 = cos * dx + sin * dy;
    const y1 = -sin * dx + cos * dy;
    rx = Math.abs(rx);
    ry = Math.abs(ry);
    const lambda = (x1 * x1) / (rx * rx) + (y1 * y1) / (ry * ry);
    if (lambda > 1) {
        rx *= Math.sqrt(lambda);
        ry *= Math.sqrt(lambda);
    }
    const num = rx * rx * ry * ry - rx * rx * y1 * y1 - ry * ry * x1 * x1;
    const den = rx * rx * y1 * y1 + ry * ry * x1 * x1;
    const coef = (large !== sweep ? 1 : -1) * Math.sqrt(Math.max(0, num / den));
    const cx1 = (coef * rx * y1) / ry;
    const cy1 = (-coef * ry * x1) / rx;
    const cx = cos * cx1 - sin * cy1 + (p0[0] + p1[0]) / 2;
    const cy = sin * cx1 + cos * cy1 + (p0[1] + p1[1]) / 2;
    const angle = (ux: number, uy: number, vx: number, vy: number) => Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
    const theta1 = angle(1, 0, (x1 - cx1) / rx, (y1 - cy1) / ry);
    let delta = angle((x1 - cx1) / rx, (y1 - cy1) / ry, (-x1 - cx1) / rx, (-y1 - cy1) / ry);
    if (!sweep && delta > 0)
        delta -= 2 * Math.PI;
    if (sweep && delta < 0)
        delta += 2 * Math.PI;
    const n = segmentsFor(Math.abs(delta) * Math.max(rx, ry), Math.abs(delta));
    for (let i = 1; i <= n; i++) {
        const a = theta1 + (delta * i) / n;
        const ex = rx * Math.cos(a);
        const ey = ry * Math.sin(a);
        out.push([cos * ex - sin * ey + cx, sin * ex + cos * ey + cy]);
    }
}
/** Parse SVG path data (all commands, absolute and relative) into flattened subpaths. */
export function parsePath(d: string): Subpath[] {
    const tokens = tokenize(d);
    const subpaths: Subpath[] = [];
    let current: Subpath | null = null;
    let pos: Pt = [0, 0];
    let start: Pt = [0, 0];
    let lastCtrl: Pt | null = null;
    let lastCmd = '';
    let i = 0;
    const num = () => {
        const v = tokens[i++];
        if (typeof v !== 'number')
            throw new Error('invalid path data: expected a number');
        return v;
    };
    const begin = (p: Pt) => {
        current = { points: [p], closed: false };
        subpaths.push(current);
    };
    const push = (pts: Pt[]) => {
        if (!current)
            begin(pos);
        current!.points.push(...pts);
    };
    let cmd = '';
    while (i < tokens.length) {
        const t = tokens[i];
        if (typeof t === 'string') {
            cmd = t;
            i++;
        }
        else if (cmd === 'M')
            cmd = 'L';
        else if (cmd === 'm')
            cmd = 'l';
        else if (cmd === 'Z' || cmd === 'z')
            throw new Error('invalid path data: number after Z');
        if (!cmd)
            throw new Error('invalid path data: missing command');
        const rel = cmd === cmd.toLowerCase();
        const ox = rel ? pos[0] : 0;
        const oy = rel ? pos[1] : 0;
        switch (cmd.toUpperCase()) {
            case 'M': {
                pos = [ox + num(), oy + num()];
                start = pos;
                begin(pos);
                lastCtrl = null;
                break;
            }
            case 'L': {
                pos = [ox + num(), oy + num()];
                push([pos]);
                lastCtrl = null;
                break;
            }
            case 'H': {
                pos = [(rel ? pos[0] : 0) + num(), pos[1]];
                push([pos]);
                lastCtrl = null;
                break;
            }
            case 'V': {
                pos = [pos[0], (rel ? pos[1] : 0) + num()];
                push([pos]);
                lastCtrl = null;
                break;
            }
            case 'C': {
                const c1: Pt = [ox + num(), oy + num()];
                const c2: Pt = [ox + num(), oy + num()];
                const end: Pt = [ox + num(), oy + num()];
                const pts: Pt[] = [];
                cubic(pos, c1, c2, end, pts);
                push(pts);
                lastCtrl = c2;
                pos = end;
                break;
            }
            case 'S': {
                const c1: Pt = lastCtrl && /[CcSs]/.test(lastCmd) ? [2 * pos[0] - lastCtrl[0], 2 * pos[1] - lastCtrl[1]] : pos;
                const c2: Pt = [ox + num(), oy + num()];
                const end: Pt = [ox + num(), oy + num()];
                const pts: Pt[] = [];
                cubic(pos, c1, c2, end, pts);
                push(pts);
                lastCtrl = c2;
                pos = end;
                break;
            }
            case 'Q': {
                const c: Pt = [ox + num(), oy + num()];
                const end: Pt = [ox + num(), oy + num()];
                const pts: Pt[] = [];
                quad(pos, c, end, pts);
                push(pts);
                lastCtrl = c;
                pos = end;
                break;
            }
            case 'T': {
                const c: Pt = lastCtrl && /[QqTt]/.test(lastCmd) ? [2 * pos[0] - lastCtrl[0], 2 * pos[1] - lastCtrl[1]] : pos;
                const end: Pt = [ox + num(), oy + num()];
                const pts: Pt[] = [];
                quad(pos, c, end, pts);
                push(pts);
                lastCtrl = c;
                pos = end;
                break;
            }
            case 'A': {
                const rx = num();
                const ry = num();
                const phi = num();
                const large = num() !== 0;
                const sweep = num() !== 0;
                const end: Pt = [ox + num(), oy + num()];
                const pts: Pt[] = [];
                arc(pos, rx, ry, phi, large, sweep, end, pts);
                push(pts);
                lastCtrl = null;
                pos = end;
                break;
            }
            case 'Z': {
                if (current) {
                    (current as Subpath).closed = true;
                    (current as Subpath).points.push(start);
                }
                pos = start;
                current = null;
                lastCtrl = null;
                break;
            }
            default:
                throw new Error(`unsupported path command ${cmd}`);
        }
        lastCmd = cmd;
    }
    return subpaths.filter((s) => s.points.length > 1);
}
export const dist = (a: Pt, b: Pt) => Math.hypot(b[0] - a[0], b[1] - a[1]);
export function length(sub: Subpath): number {
    let l = 0;
    for (let i = 1; i < sub.points.length; i++)
        l += dist(sub.points[i - 1]!, sub.points[i]!);
    return l;
}
export function totalLength(subs: Subpath[]): number {
    return subs.reduce((a, s) => a + length(s), 0);
}
export function bounds(subs: Subpath[]): Box {
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (const s of subs)
        for (const [x, y] of s.points) {
            x0 = Math.min(x0, x);
            y0 = Math.min(y0, y);
            x1 = Math.max(x1, x);
            y1 = Math.max(y1, y);
        }
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
/** Uniformly scale and centre `subs` into `target` (contain fit). */
export function fit(subs: Subpath[], target: Box, box: Box = bounds(subs)): Subpath[] {
    const s = Math.min(target.w / Math.max(box.w, 1e-9), target.h / Math.max(box.h, 1e-9));
    const ox = target.x + (target.w - box.w * s) / 2 - box.x * s;
    const oy = target.y + (target.h - box.h * s) / 2 - box.y * s;
    return subs.map((sub) => ({
        closed: sub.closed,
        points: sub.points.map(([x, y]) => [x * s + ox, y * s + oy] as Pt),
    }));
}
/** Point at fraction `t` (0–1) of a subpath's length. */
export function pointAt(sub: Subpath, t: number): Pt {
    const target = Math.min(1, Math.max(0, t)) * length(sub);
    let acc = 0;
    for (let i = 1; i < sub.points.length; i++) {
        const a = sub.points[i - 1]!;
        const b = sub.points[i]!;
        const d = dist(a, b);
        if (acc + d >= target) {
            const k = d === 0 ? 0 : (target - acc) / d;
            return [lerp(a[0], b[0], k), lerp(a[1], b[1], k)];
        }
        acc += d;
    }
    return sub.points[sub.points.length - 1]!;
}
/** Resample a subpath to exactly `n` evenly spaced points (for morphing). */
export function resample(sub: Subpath, n: number): Pt[] {
    return Array.from({ length: n }, (_, i) => pointAt(sub, n === 1 ? 0 : i / (sub.closed ? n : n - 1)));
}
/** `count` points spread evenly by length across all subpaths (for particles along a logo). */
export function scatterAlong(subs: Subpath[], count: number): Pt[] {
    const lens = subs.map(length);
    const total = lens.reduce((a, b) => a + b, 0);
    const out: Pt[] = [];
    subs.forEach((s, i) => {
        const n = Math.max(1, Math.round((count * lens[i]!) / total));
        for (let k = 0; k < n; k++)
            out.push(pointAt(s, (k + 0.5) / n));
    });
    return out;
}
/** Interpolate between two equal-length point lists. */
export function morph(a: Pt[], b: Pt[], t: number): Pt[] {
    return a.map((p, i) => [lerp(p[0], b[i]![0], t), lerp(p[1], b[i]![1], t)] as Pt);
}
/**
 * Stroke the first `progress` (0–1) of each subpath — the classic draw-on. `mode: 'sequential'`
 * draws subpaths one after another; `'parallel'` grows them all at once.
 */
export function strokeProgress(ctx: CanvasRenderingContext2D, subs: Subpath[], progress: number, mode: 'parallel' | 'sequential' = 'parallel'): void {
    if (progress <= 0)
        return;
    const lens = subs.map(length);
    const total = lens.reduce((a, b) => a + b, 0);
    let budget = progress * total;
    ctx.beginPath();
    subs.forEach((s, i) => {
        const want = mode === 'parallel' ? progress * lens[i]! : Math.min(lens[i]!, Math.max(0, budget));
        budget -= lens[i]!;
        if (want <= 0)
            return;
        let acc = 0;
        ctx.moveTo(s.points[0]![0], s.points[0]![1]);
        for (let k = 1; k < s.points.length; k++) {
            const a = s.points[k - 1]!;
            const b = s.points[k]!;
            const d = dist(a, b);
            if (acc + d >= want) {
                const f = d === 0 ? 0 : (want - acc) / d;
                ctx.lineTo(lerp(a[0], b[0], f), lerp(a[1], b[1], f));
                break;
            }
            ctx.lineTo(b[0], b[1]);
            acc += d;
        }
    });
    ctx.stroke();
}
/** Trace subpaths into the current path (for fill / clip). */
export function tracePath(ctx: CanvasRenderingContext2D, subs: Subpath[]): void {
    ctx.beginPath();
    for (const s of subs) {
        ctx.moveTo(s.points[0]![0], s.points[0]![1]);
        for (let k = 1; k < s.points.length; k++)
            ctx.lineTo(s.points[k]![0], s.points[k]![1]);
        if (s.closed)
            ctx.closePath();
    }
}
